'use client';

import { useState, useEffect } from 'react';

import { faUser } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouter } from 'next/navigation';

import AuthLayout from '@/components/auth/AuthLayout';
import Divider from '@/components/auth/Divider';
import GoogleSignIn from '@/components/auth/GoogleSignIn';
import OTPVerification from '@/components/auth/OTPVerification';
import SignUpForm from '@/components/auth/SignUpForm';
import ErrorMessage from '@/components/common/ErrorMessage';
import { Spinner } from '@/components/common/Spinner';
import {
  checkEmail,
  requestLoginOTP,
  loginWithOTP,
  setIsSignUp,
  setRequestAction,
} from '@/features/auth/authSlice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

interface LoginFormData {
  email: string;
}

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { isSignUp } = useAppSelector((state) => state.auth);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [emailExists, setEmailExists] = useState<boolean | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [checkEmailLoading, setCheckEmailLoading] = useState(false);
  const [shouldCheck, setShouldCheck] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!formData.email || !shouldCheck) return;

      try {
        setCheckEmailLoading(true);
        const result = await dispatch(checkEmail(formData.email)).unwrap();
        setEmailExists(result);
      } catch (error) {
        setEmailExists(false);
      } finally {
        setCheckEmailLoading(false);
        setIsTyping(false);
      }
    };

    const timeoutId = setTimeout(verifyEmail, 1000);
    return () => clearTimeout(timeoutId);
  }, [formData.email, dispatch, shouldCheck]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setFormData({ email });
    setIsTyping(true);
    setShouldCheck(email.includes('@'));
    setError('');

    if (!email.includes('@')) {
      setEmailExists(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await dispatch(requestLoginOTP(formData.email)).unwrap();
      setShowOTPVerification(true);
    } catch (error: any) {
      setError(error.message || 'Failed to send login code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPVerify = async (otp: string) => {
    try {
      await dispatch(loginWithOTP({ email: formData.email, otp })).unwrap();
      router.push('/notice-dashboard');
    } catch (error: any) {
      throw new Error(error.message || 'Failed to verify OTP');
    }
  };

  if (showOTPVerification) {
    return (
      <AuthLayout>
        <OTPVerification
          email={formData.email}
          onBack={() => setShowOTPVerification(false)}
          onVerify={handleOTPVerify}
          onSuccess={() => router.push('/notice-dashboard')}
        />
      </AuthLayout>
    );
  }

  const handleSignIn = () => {
    dispatch(setIsSignUp(false));
    dispatch(setRequestAction(null));
    router.push('/auth/login');
  };

  return (
    <AuthLayout>
      {isSignUp ? (
        <SignUpForm onSignInClick={handleSignIn} />
      ) : (
        <div className="w-full flex flex-col items-center">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back!</h2>
            <p className="text-sm text-gray-500">
              Sign in to access your account
            </p>
          </div>

          {/* Google Sign In */}
          <div className="w-full mb-6">
            <GoogleSignIn />
          </div>

          <Divider />

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="w-full mt-6 space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Or continue with email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faUser} className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleEmailChange}
                  className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-all text-gray-900 placeholder:text-gray-400"
                  placeholder="Enter your email address"
                />
              </div>
              <div className="mt-2 min-h-[20px] flex items-center">
                {shouldCheck && checkEmailLoading && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Spinner size="small" />
                    <span>Checking email...</span>
                  </div>
                )}
                {emailExists === false && !isTyping && (
                  <p className="text-sm text-red-500">Email not found. Please sign up first.</p>
                )}
              </div>
            </div>

            {error && <ErrorMessage message={error} />}

            <button
              type="submit"
              disabled={isLoading || !formData.email || emailExists === false}
              className="w-full bg-[#0D60E6] text-white py-3.5 rounded-xl font-semibold hover:bg-[#0D52C2] disabled:bg-gray-300 disabled:cursor-not-allowed transition-all"
            >
              {isLoading ? 'Sending code...' : 'Continue with Email'}
            </button>

            <div className="text-center text-sm pt-2">
              <span className="text-gray-500">Don&apos;t have an account? </span>
              <button
                type="button"
                onClick={() => dispatch(setIsSignUp(true))}
                className="text-[#0D60E6] hover:text-[#0D52C2] font-semibold"
              >
                Sign up
              </button>
            </div>
          </form>
        </div>
      )}
    </AuthLayout>
  );
}
